/**
 * Tiptap 에디터 공용 툴바
 * - 기본 서식 (Bold, Italic, Strike, Heading, List)
 * - 텍스트 색상 / 형광펜
 * - 이미지 업로드
 */
import { useState, useRef, useEffect } from 'react';
import {
  Bold, Italic, Strikethrough, Heading1, Heading2,
  List, ListOrdered, ImagePlus, Palette, Highlighter, FileCode2,
} from 'lucide-react';

const TEXT_COLORS = [
  { label: '기본', value: null },
  { label: '빨강', value: '#ef4444' },
  { label: '주황', value: '#f97316' },
  { label: '노랑', value: '#eab308' },
  { label: '초록', value: '#22c55e' },
  { label: '파랑', value: '#3b82f6' },
  { label: '보라', value: '#8b5cf6' },
  { label: '핑크', value: '#ec4899' },
  { label: '흰색', value: '#ffffff' },
  { label: '회색', value: '#9ca3af' },
];

const HIGHLIGHT_COLORS = [
  { label: '없음', value: null },
  { label: '노랑', value: '#fef08a' },
  { label: '초록', value: '#bbf7d0' },
  { label: '파랑', value: '#bfdbfe' },
  { label: '보라', value: '#ddd6fe' },
  { label: '핑크', value: '#fbcfe8' },
  { label: '주황', value: '#fed7aa' },
  { label: '빨강', value: '#fecaca' },
];

function ColorDropdown({ colors, activeColor, onSelect, onClose, triggerRef }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (triggerRef?.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    const handler = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        triggerRef?.current && !triggerRef.current.contains(e.target)
      ) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
      className="bg-gray-800 border border-gray-700 rounded-lg p-2 grid grid-cols-5 gap-1 min-w-[140px]"
    >
      {colors.map((c) => (
        <button
          key={c.label}
          title={c.label}
          onClick={() => { onSelect(c.value); onClose(); }}
          className={`w-6 h-6 rounded border flex items-center justify-center ${
            activeColor === c.value ? 'border-white ring-1 ring-white' : 'border-gray-600 hover:border-gray-400'
          }`}
          style={{ backgroundColor: c.value || 'transparent' }}
        >
          {!c.value && <span className="text-gray-400 text-[10px]">✕</span>}
        </button>
      ))}
    </div>
  );
}

export default function EditorToolbar({ editor, onImageUpload, onMarkdownUpload }) {
  const [showTextColor, setShowTextColor] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const textColorBtnRef = useRef(null);
  const highlightBtnRef = useRef(null);

  if (!editor) return null;

  const btn = (active, onClick, Icon, title) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded ${active ? 'bg-brand/20 text-brand' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
      <Icon size={16} />
    </button>
  );

  const activeTextColor = editor.getAttributes('textStyle').color || null;
  const activeHighlight = editor.getAttributes('highlight').color || null;

  return (
    <div className="flex items-center gap-0.5 px-3 py-2 border-b border-gray-800 bg-gray-900/50 overflow-x-auto scrollbar-none flex-nowrap">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, '굵게')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, '기울임')}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), Strikethrough, '취소선')}
      <div className="w-px h-5 bg-gray-700 mx-1" />
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), Heading1, '제목 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, '제목 2')}
      <div className="w-px h-5 bg-gray-700 mx-1" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, '글머리 기호')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, '번호 목록')}
      <div className="w-px h-5 bg-gray-700 mx-1" />

      {/* 텍스트 색상 */}
      <div className="relative">
        <button
          ref={textColorBtnRef}
          onClick={() => { setShowTextColor(!showTextColor); setShowHighlight(false); }}
          title="글자 색상"
          className={`p-1.5 rounded flex items-center gap-0.5 ${showTextColor ? 'bg-brand/20 text-brand' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
        >
          <Palette size={16} />
          {activeTextColor && (
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: activeTextColor }} />
          )}
        </button>
        {showTextColor && (
          <ColorDropdown
            colors={TEXT_COLORS}
            activeColor={activeTextColor}
            triggerRef={textColorBtnRef}
            onSelect={(color) => {
              if (color) editor.chain().focus().setColor(color).run();
              else editor.chain().focus().unsetColor().run();
            }}
            onClose={() => setShowTextColor(false)}
          />
        )}
      </div>

      {/* 형광펜 */}
      <div className="relative">
        <button
          ref={highlightBtnRef}
          onClick={() => { setShowHighlight(!showHighlight); setShowTextColor(false); }}
          title="형광펜"
          className={`p-1.5 rounded flex items-center gap-0.5 ${showHighlight ? 'bg-brand/20 text-brand' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
        >
          <Highlighter size={16} />
          {activeHighlight && (
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: activeHighlight }} />
          )}
        </button>
        {showHighlight && (
          <ColorDropdown
            colors={HIGHLIGHT_COLORS}
            activeColor={activeHighlight}
            triggerRef={highlightBtnRef}
            onSelect={(color) => {
              if (color) editor.chain().focus().toggleHighlight({ color }).run();
              else editor.chain().focus().unsetHighlight().run();
            }}
            onClose={() => setShowHighlight(false)}
          />
        )}
      </div>

      <div className="w-px h-5 bg-gray-700 mx-1" />
      <button onClick={onImageUpload} title="이미지" className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800">
        <ImagePlus size={16} />
      </button>
      {onMarkdownUpload && (
        <button onClick={onMarkdownUpload} title=".md 파일 삽입" className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 flex items-center gap-0.5">
          <FileCode2 size={16} />
          <span className="text-[10px] leading-none">MD</span>
        </button>
      )}
    </div>
  );
}
